/**
 * Application state and persistence.
 *
 * Every mutation funnels through saveLocal(): it writes localStorage (still
 * the working copy) and queues a sync when the user is signed in. Signed out,
 * nothing leaves the device.
 *
 * Views import state from here rather than from app.ts, which keeps the view
 * modules free of import cycles.
 */
import * as Sync from '../sync';
import { localStamp, type Exercise, type MetricKey, type Workout, type WorkoutData } from '../types';

const DEFAULT_EXERCISES: Exercise[] = [
  { id: '', name: 'pushups', metrics: ['reps'], clientUpdated: localStamp('') },
  { id: '', name: 'pull-ups', metrics: ['reps'], clientUpdated: localStamp('') },
  { id: '', name: 'chin-ups', metrics: ['reps'], clientUpdated: localStamp('') },
];

function loadJSON<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    // Corrupt storage should not brick the app; fall back to defaults.
    return fallback;
  }
}

export let exercises: Exercise[] = loadJSON<Exercise[]>('exercises', DEFAULT_EXERCISES);
export let workoutData: WorkoutData = loadJSON<WorkoutData>('workoutData', {});
export let currentExercise = 'pushups';

for (const exercise of exercises) {
  workoutData[exercise.name] ??= [];
}
if (!exercises.some((ex) => ex.name === currentExercise) && exercises[0]) {
  currentExercise = exercises[0].name;
}

export function setCurrentExercise(name: string): void {
  currentExercise = name;
}

export function persistLocal(): void {
  localStorage.setItem('exercises', JSON.stringify(exercises));
  localStorage.setItem('workoutData', JSON.stringify(workoutData));
}

export function saveLocal(): void {
  persistLocal();
  Sync.schedule();
}

export function currentWorkouts(): Workout[] {
  return workoutData[currentExercise] ?? [];
}

/** True when this device holds workouts that may not be in an account yet. */
export function hasLocalWorkouts(): boolean {
  return Object.values(workoutData).some((list) => (list?.length ?? 0) > 0);
}

export function metricsFor(name: string): MetricKey[] {
  return exercises.find((ex) => ex.name === name)?.metrics ?? ['reps'];
}

/**
 * A redraw hook rather than a direct import: the view modules need to trigger
 * a full refresh, and app.ts owns the implementation. Routing it through here
 * avoids a cycle between app.ts and the views.
 */
let refreshHook: () => void = () => {};

export function onRefresh(fn: () => void): void {
  refreshHook = fn;
}

export function refreshAllViews(): void {
  refreshHook();
}

/** The accessors the sync engine uses to read and replace app state. */
export const syncStore = {
  getExercises: () => exercises,
  setExercises: (value: Exercise[]) => {
    exercises = value;
  },
  getWorkoutData: () => workoutData,
  setWorkoutData: (value: WorkoutData) => {
    workoutData = value;
  },
  persist: persistLocal,
};
